// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect, useRef } from "react"
import { Bell, X, Building2, UserCheck, AlertTriangle, CheckCheck } from "lucide-react"
import { supabase } from "../lib/supabase"
import { format } from "date-fns"

interface Notification {
  id: string
  type: string
  title: string
  body: string | null
  metadata: any
  read: boolean
  created_at: string
}

const TYPE_ICON: Record<string, { icon: typeof Bell; color: string }> = {
  demo_request:    { icon: Building2,    color: "#E63946" },
  new_signup:      { icon: UserCheck,    color: "#22c55e" },
  violation_spike: { icon: AlertTriangle, color: "#f59e0b" },
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [open, setOpen]   = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const unread = notifications.filter(n => !n.read).length

  useEffect(() => {
    fetchNotifications()

    // Real-time: new notification lands → update immediately
    const channel = supabase
      .channel("notifications")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" },
        payload => setNotifications(prev => [payload.new as Notification, ...prev])
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  async function fetchNotifications() {
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(30)
    if (data) setNotifications(data)
  }

  async function markRead(id: string) {
    await supabase.from("notifications").update({ read: true }).eq("id", id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }

  async function markAllRead() {
    await supabase.from("notifications").update({ read: true }).eq("read", false)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: "relative", background: "none", border: "none",
          cursor: "pointer", padding: 6, borderRadius: 8,
          color: open ? "#fff" : "#6b7280",
          transition: "color 0.15s",
        }}
        title="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: 2, right: 2,
            width: 16, height: 16, borderRadius: "50%",
            background: "#E63946", color: "#fff",
            fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid #030712",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
          transform: "translateX(-50%)",
          width: 340, maxHeight: 480,
          background: "#0a0f1a", border: "1px solid #1e293b",
          borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
          display: "flex", flexDirection: "column",
          zIndex: 9999,
        }}>
          {/* Header */}
          <div style={{
            padding: "12px 14px", borderBottom: "1px solid #1e293b",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>
              Notifications {unread > 0 && <span style={{ color: "#E63946" }}>({unread})</span>}
            </span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 11, color: "#475569", display: "flex", alignItems: "center", gap: 4,
              }}>
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center", color: "#334155", fontSize: 13 }}>
                No notifications yet
              </div>
            ) : notifications.map(n => {
              const meta = TYPE_ICON[n.type] || { icon: Bell, color: "#60a5fa" }
              const Icon = meta.icon
              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && markRead(n.id)}
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #0f172a",
                    background: n.read ? "transparent" : "#0f1f38",
                    cursor: n.read ? "default" : "pointer",
                    transition: "background 0.12s",
                    display: "flex", gap: 10, alignItems: "flex-start",
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: meta.color + "18",
                    border: `1px solid ${meta.color}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginTop: 1,
                  }}>
                    <Icon size={13} color={meta.color} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: n.read ? 500 : 700,
                      color: n.read ? "#64748b" : "#f1f5f9",
                      marginBottom: 3, lineHeight: 1.3,
                    }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{
                        fontSize: 11, color: "#475569", lineHeight: 1.5,
                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                      }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#334155", marginTop: 4 }}>
                      {format(new Date(n.created_at), "MMM d, HH:mm")}
                    </div>
                  </div>

                  {/* Unread dot */}
                  {!n.read && (
                    <div style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: "#E63946", flexShrink: 0, marginTop: 5,
                    }} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
